// VK Bridge интеграция (замена telegram.ts)
import bridge, { UserInfo } from '@vkontakte/vk-bridge';
import { VKUser } from '../types';

// Кешируем данные пользователя
let cachedUser: VKUser | null = null;

// Ранняя инициализация — вызываем VKWebAppInit немедленно при загрузке модуля
bridge.send('VKWebAppInit')
  .then(() => console.log('✅ VK Mini App инициализировано'))
  .catch((err) => console.warn('VKWebAppInit error:', err));

// Инициализация VK Mini App (повторный вызов для совместимости)
export const initVKApp = async (): Promise<boolean> => {
  try {
    await bridge.send('VKWebAppInit');
    return true;
  } catch (error) {
    console.warn('Ошибка инициализации VK Mini App:', error);
    return false;
  }
};

// Получить данные пользователя VK
export const getVKUser = async (): Promise<VKUser | null> => {
  if (cachedUser) return cachedUser;

  try {
    const userInfo: UserInfo = await bridge.send('VKWebAppGetUserInfo');
    cachedUser = {
      id: userInfo.id,
      first_name: userInfo.first_name,
      last_name: userInfo.last_name,
      photo_100: userInfo.photo_100,
      photo_200: userInfo.photo_200,
    };
    return cachedUser;
  } catch (error) {
    console.warn('Не удалось получить данные пользователя VK:', error);
    
    // Для тестирования в браузере
    if (typeof window !== 'undefined') {
      return {
        id: 123456789,
        first_name: 'Тестовый',
        last_name: 'Пользователь',
      };
    }
    
    return null;
  }
};

// Получить или создать гостя (аналог getOrCreateGuest)
export const getOrCreateGuest = async (): Promise<VKUser> => {
  const vkUser = await getVKUser();
  
  if (vkUser) {
    return vkUser;
  }
  
  // Создаем гостя с уникальным ID
  const guestId = Math.floor(Math.random() * 1000000) + 100000;
  const guestNumber = Math.floor(Math.random() * 100) + 1;
  
  return {
    id: guestId,
    first_name: `Гость ${guestNumber}`,
    last_name: '',
  };
};

// Синхронная версия для случаев, когда пользователь уже загружен
export const getCachedUser = (): VKUser | null => {
  return cachedUser;
};

// Показать alert (аналог showTelegramAlert)
export const showAlert = async (message: string) => {
  try {
    await bridge.send('VKWebAppTapticNotificationOccurred', { type: 'warning' });
  } catch (_) { /* ignore */ }
  
  // VK Bridge не имеет встроенного alert, используем нативный
  alert(message);
};

// Показать confirm
export const showConfirm = (message: string): Promise<boolean> => {
  return Promise.resolve(window.confirm(message));
};

// Haptic feedback (аналог hapticFeedback)
export const hapticFeedback = {
  impact: async (style: 'light' | 'medium' | 'heavy' = 'medium') => {
    try {
      await bridge.send('VKWebAppTapticImpactOccurred', { style });
    } catch (_) { /* ignore */ }
  },
  notification: async (type: 'error' | 'success' | 'warning') => {
    try {
      await bridge.send('VKWebAppTapticNotificationOccurred', { type });
    } catch (_) { /* ignore */ }
  },
  selection: async () => {
    try {
      await bridge.send('VKWebAppTapticSelectionChanged');
    } catch (_) { /* ignore */ }
  }
};

// Поделиться ссылкой через VK (нативный диалог)
export const shareLink = async (link: string, message?: string) => {
  try {
    await bridge.send('VKWebAppShare', { link });
  } catch (error) {
    console.warn('VKWebAppShare не поддерживается, копируем в буфер:', error);
    await copyToClipboard(link);
  }
};

// Пригласить друзей через нативный диалог шеринга VK
export const inviteFriends = async (boardId: string, boardTitle: string) => {
  const appId = process.env.REACT_APP_VK_APP_ID || '';
  const link = `https://vk.com/app${appId}#join_${boardId}`;
  
  try {
    // VKWebAppShare — единственный надёжный способ поделиться ссылкой в VK Mini App
    // Открывает нативное окно VK: пользователь выбирает друга/беседу/стену
    await bridge.send('VKWebAppShare', { link });
    return true;
  } catch (error) {
    console.warn('VKWebAppShare ошибка:', error);
    // Fallback — копируем ссылку
    await copyToClipboard(link);
    return false;
  }
};

// Скопировать ссылку в буфер (без VK диалога подтверждения)
export const copyToClipboard = async (text: string) => {
  try {
    // Сначала пробуем стандартный API — без диалога "Подтвердите действие"
    await navigator.clipboard.writeText(text);
    alert('✅ Ссылка скопирована!\n\nОтправь её друзьям — каждый сможет отметить свой заказ 🍽️');
  } catch (_) {
    try {
      // Fallback через VK Bridge (покажет диалог подтверждения)
      await bridge.send('VKWebAppCopyText', { text });
      alert('✅ Ссылка скопирована!\n\nОтправь её друзьям — каждый сможет отметить свой заказ 🍽️');
    } catch (__) {
      alert(`📋 Скопируй ссылку и отправь друзьям:\n\n${text}`);
    }
  }
};

// Получить ссылку приглашения для VK Mini App
export const getInviteLink = (boardId: string): string => {
  const appId = process.env.REACT_APP_VK_APP_ID || '';
  return `https://vk.com/app${appId}#join_${boardId}`;
};

// Закрыть приложение
export const closeApp = async () => {
  try {
    await bridge.send('VKWebAppClose', { status: 'success' });
  } catch (error) {
    console.warn('Ошибка закрытия VK Mini App:', error);
  }
};

// Получить hash из URL (для deep link)
export const getHashParams = (): string => {
  return window.location.hash.replace('#', '');
};
